/**
 * Jobs API Service
 */

import http from '../client';
import { ENDPOINTS } from '../config';

/**
 * Get active jobs from backend with pagination and optional filters (public endpoint, no auth required)
 * Returns jobs with: id, name, job, companyName, companyPhoto, salary, createdDate, department, description
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-indexed for backend)
 * @param {number} params.size - Items per page (default 100)
 * @param {number[]} params.categoryIds - Filter by category IDs (1=Waiter, 2=Cashier, 3=Cook, 5=Store)
 * @param {boolean} params.sortBySalary - Sort by salary (highest first) if true
 * @param {number[]} params.industryIds - Filter by industry IDs (parent industry)
 * @param {number} params.minSalary - Minimum salary filter
 * @param {number} params.maxSalary - Maximum salary filter
 * @param {number[]} params.jobLevelIds - Filter by job level IDs (experience level)
 * @param {string} params.searchQuery - Text search query (searches job name, company name, job field)
 * @param {string} params.country - Filter by company country code (e.g., "UZ" for Uzbekistan jobs only)
 * @returns {Promise<Object>}
 */
export const getActiveJobs = async (params = {}) => {
  try {
    const queryParams = {
      page: params.page ?? 0,
      size: params.size ?? 100
    };
    // Ask the server to leave the ad bodies out. `introductionText` and `requirementText`
    // are the whole text of a posting and together about two thirds of this response, and
    // nothing that renders a LIST reads them — the job page fetches /job/{id} and the preview
    // drawer fetches the company's own ads. Opt-in on the server, so passing it is what makes
    // it happen; pass `full: true` on the rare call that does need the bodies.
    if (!params.full) {
      queryParams.brief = true;
    }
    // Add filter params if provided
    if (params.categoryIds && params.categoryIds.length > 0) {
      queryParams.categoryIds = params.categoryIds;
    }
    if (params.sortBySalary) {
      queryParams.sortBySalary = params.sortBySalary;
    }
    // Industry filter
    if (params.industryIds && params.industryIds.length > 0) {
      queryParams.industryIds = params.industryIds;
    }
    // Salary range filter
    if (params.minSalary != null && params.minSalary > 0) {
      queryParams.minSalary = params.minSalary;
    }
    if (params.maxSalary != null && params.maxSalary < 10000000) {
      queryParams.maxSalary = params.maxSalary;
    }
    // Job level filter
    if (params.jobLevelIds && params.jobLevelIds.length > 0) {
      queryParams.jobLevelIds = params.jobLevelIds;
    }
    // Text search filter
    if (params.searchQuery && params.searchQuery.trim()) {
      queryParams.searchQuery = params.searchQuery.trim();
    }
    // Country filter (e.g., "UZ" for Uzbekistan)
    if (params.country && params.country.trim()) {
      queryParams.country = params.country.trim().toUpperCase();
    }
    // Employment type filter (e.g., "PART_TIME", "FULL_TIME", "CONTRACT")
    if (params.employmentType && params.employmentType.trim()) {
      queryParams.employmentType = params.employmentType.trim();
    }
    // Working days filter (e.g., ["MON","WED","FRI"])
    if (params.workingDays && params.workingDays.length > 0) {
      queryParams.workingDays = params.workingDays.join(",");
    }
    // Each job's weekly schedule beside the page, as `schedules: { jobId: [...] }` — only
    // the availability calendar needs it, so it is asked for rather than always sent
    if (params.withSchedules) {
      queryParams.withSchedules = true;
    }
    // Company filter (e.g., the home page's recent-company rail)
    if (params.companyIds && params.companyIds.length > 0) {
      queryParams.companyIds = params.companyIds;
    }
    // Date order — the backend returns newest first unless asked for "asc"
    if (params.sortOrder === 'asc') {
      queryParams.sortOrder = 'asc';
    }
    const response = await http.get(ENDPOINTS.JOBS.ACTIVE, queryParams, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get the home page's counts and company rail in one call (public endpoint, no auth required)
 * Returns: industryCounts ({industryId: n}), partTimeCount, sectorCounts ({slug: n}),
 * recentCompanies ([{id, name, logo, count, latest}])
 * @param {string} country - Company country code (e.g., "MN")
 * @returns {Promise<Object>}
 */
export const getJobsHomeMeta = async (country) => {
  try {
    const response = await http.get(ENDPOINTS.JOBS.HOME_META, country ? { country } : null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * How many open ads each industry has, keyed by industry id.
 *
 * One request for all of them. The home page orders its industry chips by this, and used to
 * learn it by calling getActiveJobs once per industry with size=1 — 27 requests that the
 * browser could only run six at a time, plus 27 COUNT queries on the server, every page load.
 *
 * @param {string} [country] ISO country code, or omit for every country
 * @returns {Promise<Object>} { success, data: { [industryId]: count } }
 */
export const getJobCountsByIndustry = async (country) => {
  try {
    return await http.get(
      ENDPOINTS.JOBS.COUNT_BY_INDUSTRY,
      country ? { country } : {},
      { skipAuth: true },
    );
  } catch (error) {
    return { success: false, error: error.message, data: {} };
  }
};

/**
 * Get job detail
 * @param {string|number} jobId
 * @returns {Promise<Object>}
 */
export const getJobDetail = async (jobId) => {
  try {
    const response = await http.get(ENDPOINTS.JOBS.JOB_BY_ID(jobId), null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get day schedules for a part-time assessment
 * @param {string|number} assessmentId
 * @returns {Promise<Object>}
 */
export const getDaySchedules = async (assessmentId) => {
  try {
    const response = await http.get(`/assessment/${assessmentId}/day-schedules`, null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, data: [] };
  }
};

/**
 * Get all active jobs/assessments from a specific company
 * @param {string|number} companyId
 * @returns {Promise<Object>}
 */
export const getCompanyAssessments = async (companyId) => {
  try {
    const response = await http.get(ENDPOINTS.ASSESSMENT_COMPANIES.JOBS(companyId), null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get branches for a company (public endpoint)
 * @param {string|number} companyId
 * @returns {Promise<Object>}
 */
export const getCompanyBranches = async (companyId) => {
  try {
    const response = await http.get(ENDPOINTS.BRANCHES.BY_COMPANY(companyId), null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Applicant list for one job, as anyone with the company's share link sees it — no
 * login, and no phone/email/status (see PublicApplicantDto on the backend).
 * @param {string|number} companyId
 * @param {string|number} jobId
 * @returns {Promise<Object>}
 */
export const getPublicJobApplicants = async (companyId, jobId) => {
  try {
    const response = await http.get(ENDPOINTS.ASSESSMENT_COMPANIES.JOB_APPLICANTS_PUBLIC(companyId, jobId), null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * One applicant's CV, opened from a company's public share link.
 *
 * Authenticated (any signed-in candidate) rather than public — deliberately not
 * `skipAuth`, so the caller's own requireAuth()/LoginModal flow must complete first.
 * Not ownership-scoped: any verified visitor may open it, not just the ad's owner.
 * @param {string|number} companyId
 * @param {string|number} jobId
 * @param {string|number} candidateId
 * @returns {Promise<Object>}
 */
export const getAuthedApplicantCv = async (companyId, jobId, candidateId) => {
  try {
    const response = await http.get(ENDPOINTS.ASSESSMENT_COMPANIES.APPLICANT_CV_AUTH(companyId, jobId, candidateId));
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default {
  getActiveJobs,
  getJobsHomeMeta,
  getJobCountsByIndustry,
  getJobDetail,
  getDaySchedules,
  getCompanyAssessments,
  getCompanyBranches,
  getPublicJobApplicants,
  getAuthedApplicantCv
};
